import Colors from '@digili-ui/constants/colors'
import { getIfpTranslationForState } from '@sec-loans-ui/common/steps/loan-catalog-step/loanCatalogStepUtils'
import { ApplicationDetails } from '@sec-loans-ui/common/steps/loan-catalog-step/types'
import { ApplicationSelectData, ApplicationType } from '@sec-loans-ui/common/types'
import { getSecLoansLocalizedEnums } from '@sec-loans-ui/utils/getSecLoansLocalizedEnums'
import {
  IfpApplicationMetadata,
  IfpApplicationState,
  SecLoanApplicationState,
  SecLoansApplicationMetadata,
} from '@shared/sec-loans'
import { formatFinnishCurrencyWithSymbol, getLanguage } from '@ui-common/utils'
import { Card, CardIconType } from '@ui-components/containers/Card'
import { useId } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

const RootCard = styled(Card)`
  > div {
    flex-wrap: nowrap;
  }
`

export const ApplicationCard = ({
  userSsn,
  applicationDetails,
  onApplicationSelect,
}: {
  userSsn: string
  applicationDetails: ApplicationDetails
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  switch (applicationDetails.applicationType) {
    case ApplicationType.LoanApplication:
      return (
        <LoanApplicationCard
          applicationMetadata={applicationDetails.applicationMetadata}
          onApplicationSelect={onApplicationSelect}
        />
      )
    case ApplicationType.InstalmentFreePeriodApplication:
      return (
        <IfpApplicationCard
          userSsn={userSsn}
          applicationMetadata={applicationDetails.applicationMetadata}
          onApplicationSelect={onApplicationSelect}
        />
      )
  }
}

const applicationStateIcon: Record<IfpApplicationState | SecLoanApplicationState, CardIconType> = {
  Accepted: 'bankingPayslipIcon',
  Rejected: 'bankingPayslipIcon',
  Cancelled: 'documentTextIcon',
  CancelledByPrimaryApplicant: 'documentTextIcon',
  CancelledBySecondaryApplicant: 'documentTextIcon',
  Draft: 'documentEditIcon',
  ReturnedByCoDebtor: 'documentEditIcon',
  ReturnedBySecondaryApplicant: 'documentEditIcon',
  Pending: 'documentHistoryIcon',
  Sending: 'documentHistoryIcon',
  WaitingForCoDebtor: 'documentHistoryIcon',
  WaitingForResend: 'documentHistoryIcon',
  WaitingForSecondaryApplicant: 'documentHistoryIcon',
}

type BaseApplicationCardProps = {
  applicationId: string
  onApplicationSelect: () => void
  title: string
  subtitle: string
  description: string
} & (
  | {
      applicationState: SecLoanApplicationState
      applicationType: ApplicationType.LoanApplication
    }
  | {
      applicationState: IfpApplicationState
      applicationType: ApplicationType.InstalmentFreePeriodApplication
    }
)

const BaseApplicationCard = ({
  applicationId,
  applicationState,
  applicationType,
  onApplicationSelect,
  title,
  subtitle,
  description,
}: BaseApplicationCardProps) => {
  const idPrefix = `${useId()}_${applicationId}`
  const titleId = `${idPrefix}_card-title-id`
  const subtitleId = `${idPrefix}_card-subtitle-id`

  let iconBackgroundColor = Colors.BackgroundWeakPrimary
  let iconColor = Colors.ElementStrongPrimary
  if (
    [
      IfpApplicationState.Cancelled,
      SecLoanApplicationState.CancelledByPrimaryApplicant,
      SecLoanApplicationState.CancelledBySecondaryApplicant,
    ].includes(applicationState)
  ) {
    iconBackgroundColor = Colors.BackgroundWeakNeutral
    iconColor = Colors.ElementMediumNeutral
  }

  const isNotClickable =
    (applicationType === ApplicationType.LoanApplication &&
      [
        SecLoanApplicationState.CancelledByPrimaryApplicant,
        SecLoanApplicationState.CancelledBySecondaryApplicant,
      ].includes(applicationState)) ||
    (applicationType === ApplicationType.InstalmentFreePeriodApplication &&
      [IfpApplicationState.Cancelled, IfpApplicationState.Accepted].includes(applicationState))

  return (
    <RootCard
      icon={applicationStateIcon[applicationState]}
      iconBackgroundColor={iconBackgroundColor}
      iconColor={iconColor}
      tagName="li"
      aria-labelledby={titleId}
      variant={isNotClickable ? 'basic' : 'primary'}
      maxWidth={'max(500px, 50%)'}
      title={{
        text: title,
        id: titleId,
        $variant: 'heading',
        $tagName: 'h3',
      }}
      subtitle={{
        id: subtitleId,
        text: subtitle,
      }}
      key={applicationId}
      clickOptions={{
        kind: 'content',
        onClick: onApplicationSelect,
        ariaDescribedBy: `${titleId} ${subtitleId}`,
      }}
      description={description}
    />
  )
}

const LoanApplicationCard = ({
  applicationMetadata,
  onApplicationSelect,
}: {
  applicationMetadata: SecLoansApplicationMetadata
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  const { formatMessage } = useIntl()
  const { loanPurposeTypeLabelTexts, applicationTypeLabelTexts, applicationStateTexts } =
    getSecLoansLocalizedEnums(formatMessage)
  const selectData: ApplicationSelectData = {
    applicationId: applicationMetadata.applicationId,
    applicationType: ApplicationType.LoanApplication,
    data: {
      applicationPurpose: applicationMetadata.applicationPurpose,
      applicationType: applicationMetadata.applicationType,
    },
  }
  return (
    <BaseApplicationCard
      key={applicationMetadata.applicationId}
      onApplicationSelect={() => onApplicationSelect(selectData)}
      applicationId={applicationMetadata.applicationId}
      applicationState={applicationMetadata.applicationState}
      applicationType={ApplicationType.LoanApplication}
      title={applicationTypeLabelTexts[applicationMetadata.applicationType]}
      subtitle={loanPurposeTypeLabelTexts[applicationMetadata.applicationPurpose]}
      description={applicationStateTexts[applicationMetadata.applicationState]}
    />
  )
}

const IfpApplicationCard = ({
  userSsn,
  applicationMetadata,
  onApplicationSelect,
}: {
  userSsn: string
  applicationMetadata: IfpApplicationMetadata
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  const selectData: ApplicationSelectData = {
    applicationId: applicationMetadata.applicationId,
    applicationType: ApplicationType.InstalmentFreePeriodApplication,
  }
  const { formatMessage } = useIntl()
  const lang = getLanguage()
  return (
    <BaseApplicationCard
      key={applicationMetadata.applicationId}
      onApplicationSelect={() => onApplicationSelect(selectData)}
      applicationType={ApplicationType.InstalmentFreePeriodApplication}
      applicationId={applicationMetadata.applicationId}
      applicationState={applicationMetadata.state}
      title={formatMessage({ id: 'ifp-application-card-title' })}
      subtitle={`${applicationMetadata.loan.description[lang]} (${formatFinnishCurrencyWithSymbol(applicationMetadata.loan.balance)})`}
      description={getIfpTranslationForState(formatMessage, applicationMetadata, userSsn)}
    />
  )
}
